import { Euler, Group, Mesh, Object3D, Vector3 } from 'three';
import type { CharacterInstance, CharacterLoader } from './CharacterModel';

/**
 * Rybářský prut připnutý na ruku postavy.
 *
 * Animace prut neobsahují — KayKit i Quaternius animují jen kostru, takže
 * ruka svírá vzduch. Model se proto připíná na kost `hand_r` jako dítě:
 * jakmile tam visí, sleduje ruku sám a nemusí se s ním nic dopočítávat
 * ani při přehrávání animace, ani po síti.
 *
 * Je to stejná technika, jakou už používá `attachHeadPart()` pro vlasy —
 * jen jiná kost.
 */

/** Laděné umístění v prostoru kosti ruky. */
export const ROD_FIT = {
  /** Posun od středu dlaně, v metrech. */
  offset: new Vector3(0, 0.06, 0),
  /**
   * Natočení v prostoru kosti ruky.
   *
   * Model má dlouhou osu ve svém Z, kost ruky míří po své ose Y ve směru
   * prstů. Otočení o +90° kolem X srovná jedno s druhým, takže prut
   * vychází z dlaně dopředu a špička klesá k vodě.
   */
  rotation: new Euler(Math.PI / 2, 0, 0),
  /**
   * Model má přes šest metrů a Quaternius ho nedodává v reálném měřítku,
   * takže se musí zmenšit na velikost, která k postavě sedí.
   */
  scale: 0.18,
};

export type RodLevel = 1 | 2 | 3 | 4 | 5;

export class FishingRod {
  private readonly holder = new Group();
  private current: Object3D | null = null;
  private attachedTo: Object3D | null = null;

  constructor(private readonly loader: CharacterLoader) {
    this.holder.name = 'fishingRod';
    this.holder.visible = false;
  }

  get visible(): boolean {
    return this.holder.visible;
  }

  /**
   * Připne prut na pravou ruku postavy.
   *
   * Kost se hledá jménem, protože se po převlečení outfitu mění celý strom
   * meshů, ale kostra zůstává stejná — držet si referenci na objekt by
   * po přezbrojení ukazovalo do prázdna.
   */
  attach(character: CharacterInstance): boolean {
    const hand = character.root.getObjectByName('hand_r');
    if (!hand) {
      console.warn('[rod] kost hand_r nenalezena, prut nelze připnout');
      return false;
    }
    if (this.attachedTo === hand) return true;

    hand.add(this.holder);
    this.attachedTo = hand;
    this.applyFit();
    return true;
  }

  async setLevel(level: RodLevel): Promise<void> {
    const gltf = await this.loader.loadGLTF('/models/fishing/fishing_gear.glb');

    const source = gltf.scene.getObjectByName(`FishingRod_Lvl${level}`);
    if (!source) {
      console.warn(`[rod] FishingRod_Lvl${level} není v fishing_gear.glb`);
      return;
    }

    this.current?.removeFromParent();

    const rod = source.clone(true);
    rod.position.set(0, 0, 0);
    rod.rotation.set(0, 0, 0);
    rod.scale.setScalar(1);
    rod.traverse((child) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        // Prut je tenký a stín pod sebou stejně nezachytí; přijímání
        // stínu ale potřebuje, aby ve stínu stromu nesvítil.
        child.receiveShadow = true;
        child.frustumCulled = false;
      }
    });

    this.holder.add(rod);
    this.current = rod;
    this.applyFit();
  }

  show(visible: boolean): void {
    this.holder.visible = visible;
  }

  /** Přepočítá umístění podle {@link ROD_FIT}. Volat po každé změně ladění. */
  applyFit(): void {
    this.holder.position.copy(ROD_FIT.offset);
    this.holder.rotation.copy(ROD_FIT.rotation);
    this.holder.scale.setScalar(ROD_FIT.scale);
  }

  dispose(): void {
    this.holder.removeFromParent();
    this.current = null;
    this.attachedTo = null;
  }
}
